/**
 * OmegaCanonExt — iter 145
 * The 8 remaining Capability Canon UIs that ride on canon.py's new endpoints:
 *   • <QuestionEngineCard>      — reframe weak Qs → decision-value Qs
 *   • <SensingOverviewCard>     — Sensing layer health firehose
 *   • <SensingTriaxisCard>      — Materiality/Novelty/Direction news scoring
 *   • <SectorIntelCard>         — sector lens (structural read + horizon)
 *   • <StrategySelectorCard>    — pick best 2-3 strategies for a view×regime
 *   • <BestExpressionCard>      — best INSTRUMENT for a thesis
 *   • <CalibrationCard>         — actual vs assigned probability + sizing mult
 *   • <WorkedExampleCard>       — full PDF p.28 reasoning chain
 *
 * No mocks. All endpoints are real Claude (or real DB aggregation for
 * sensing/calibration). Tied to /api/omega/* on canon.py iter 145.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { fastGet } from "../lib/fastFetch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardBase = {
  borderColor: "rgba(167,139,250,0.28)",
};

const subtleBox = {
  padding: 10,
  borderRadius: 10,
  background: "var(--layer1, rgba(255,255,255,0.025))",
  border: "1px solid var(--border, rgba(255,255,255,0.06))",
};

function _err(e) {
  return e?.response?.data?.detail || e?.message || "error";
}

// ─── QuestionEngineCard (PDF p.12) ────────────────────────────────────────────
export function QuestionEngineCard() {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reframe = useCallback(async () => {
    if (q.trim().length < 4) { setError("Type at least 4 characters."); return; }
    setLoading(true); setError(null);
    try {
      const r = await axios.post(`${API}/omega/question-engine`, { question: q }, { timeout: 45000 });
      setData(r.data);
    } catch (e) { setError(_err(e)); } finally { setLoading(false); }
  }, [q]);

  return (
    <div className="card" data-testid="question-engine-card" style={cardBase}>
      <div className="sh" style={{ margin: 0 }}>Question Engine</div>
      <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, marginBottom: 10 }}>
        Reframe a weak question into the one whose answer changes a decision · PDF p.12
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input
          data-testid="qe-input"
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") reframe(); }}
          placeholder='e.g. "Is NVDA a good stock?"'
          style={{
            flex: 1, padding: "8px 12px", borderRadius: 8, fontSize: 12, fontFamily: "inherit",
            background: "var(--layer1, rgba(255,255,255,0.04))",
            border: "1px solid var(--border, rgba(255,255,255,0.1))",
            color: "var(--text)", outline: "none",
          }}
        />
        <button
          data-testid="qe-submit"
          onClick={reframe}
          disabled={loading}
          style={{
            padding: "8px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700,
            background: "#a78bfa", color: "#fff", border: "none", cursor: loading ? "wait" : "pointer",
          }}
        >{loading ? "Reframing…" : "Reframe"}</button>
      </div>
      {error && <div style={{ fontSize: 11, color: "#f87171", padding: 6 }}>{error}</div>}
      {data && (
        <div style={{ display: "grid", gap: 8 }}>
          <div data-testid="qe-reframed" style={{ ...subtleBox, borderColor: "rgba(167,139,250,0.35)" }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa", letterSpacing: 1, marginBottom: 4 }}>REFRAMED</div>
            <div style={{ fontSize: 12.5, color: "var(--text)", lineHeight: 1.6 }}>{data.reframedQuestion}</div>
          </div>
          <div style={subtleBox}>
            <div style={{ fontSize: 9, fontWeight: 800, color: "var(--muted)", letterSpacing: 1, marginBottom: 4 }}>DECISION AT STAKE</div>
            <div style={{ fontSize: 11.5, color: "var(--text)", lineHeight: 1.55 }}>{data.decisionAtStake}</div>
          </div>
          <div style={subtleBox}>
            <div style={{ fontSize: 9, fontWeight: 800, color: "var(--muted)", letterSpacing: 1, marginBottom: 6 }}>SUB-QUESTIONS THAT PAY</div>
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, color: "var(--text)", lineHeight: 1.65 }}>
              {data.keySubQuestions.map((sq, i) => <li key={i} data-testid={`qe-subq-${i}`}>{sq}</li>)}
            </ol>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", fontSize: 9, color: "var(--muted)" }}>
            <span style={{ padding: "3px 8px", borderRadius: 4, background: "var(--layer2)", letterSpacing: 1, fontWeight: 700 }}>DEPTH · {data.depthRecommended?.toUpperCase()}</span>
            {data.tickerSuggested && <span style={{ padding: "3px 8px", borderRadius: 4, background: "var(--layer2)", letterSpacing: 1, fontWeight: 700, color: "#a78bfa" }}>TICKER · {data.tickerSuggested}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SensingOverviewCard (PDF p.10) ───────────────────────────────────────────
export function SensingOverviewCard() {
  const [d, setD] = useState(null);
  useEffect(() => fastGet(`${API}/omega/sensing/overview`, setD), []);
  if (!d) return null;
  return (
    <div className="card" data-testid="sensing-overview-card" style={cardBase}>
      <div className="sh" style={{ margin: 0 }}>Sensing Layer · what Omega watches</div>
      <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, marginBottom: 10 }}>
        Firehose of the world → time-stamped picture of what is true right now · PDF p.10
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8, marginBottom: 12 }}>
        <div style={subtleBox}>
          <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 0.6, fontWeight: 700, textTransform: "uppercase" }}>News 24h</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#4ade80" }}>{d.counts.news_24h}</div>
        </div>
        <div style={subtleBox}>
          <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 0.6, fontWeight: 700, textTransform: "uppercase" }}>News 1h</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#a78bfa" }}>{d.counts.news_1h}</div>
        </div>
        <div style={subtleBox}>
          <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 0.6, fontWeight: 700, textTransform: "uppercase" }}>Open Pos</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{d.counts.positions_open}</div>
        </div>
        <div style={subtleBox}>
          <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 0.6, fontWeight: 700, textTransform: "uppercase" }}>Calib N</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{d.counts.calibration_samples}</div>
        </div>
        <div style={subtleBox}>
          <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 0.6, fontWeight: 700, textTransform: "uppercase" }}>Regime</div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#fbbf24" }}>{d.regime.current}</div>
        </div>
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        {d.watching.map((w, i) => (
          <div key={i} data-testid={`watch-${i}`} style={{ ...subtleBox, padding: 8 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: "#a78bfa", marginBottom: 2 }}>{w.category}</div>
            <div style={{ fontSize: 11, color: "var(--dim)", lineHeight: 1.5 }}>{w.what}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SensingTriaxisCard (PDF p.10) ───────────────────────────────────────────
export function SensingTriaxisCard({ symbol }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const url = symbol ? `${API}/omega/sensing/triaxis?symbol=${symbol}&limit=8` : `${API}/omega/sensing/triaxis?limit=8`;
      const r = await axios.get(url, { timeout: 60000 });
      setD(r.data);
    } catch (e) { setErr(_err(e)); } finally { setLoading(false); }
  }, [symbol]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="card" data-testid="triaxis-card" style={cardBase}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="sh" style={{ margin: 0 }}>Triaxis · {symbol || "Top news"}</div>
        <button onClick={load} data-testid="triaxis-refresh" style={{ fontSize: 9, padding: "3px 8px", border: "1px solid var(--border)", borderRadius: 999, background: "transparent", color: "var(--muted)", cursor: "pointer", fontFamily: "inherit", letterSpacing: 1 }}>{loading ? "…" : "REFRESH"}</button>
      </div>
      <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, marginBottom: 10 }}>
        Materiality × Novelty × Direction · sorted by edge score · PDF p.10
      </div>
      {err && <div style={{ fontSize: 11, color: "#f87171" }}>{err}</div>}
      {loading && !d && <div style={{ fontSize: 11.5, color: "var(--muted)" }}>Scoring axes…</div>}
      {d?.items?.length === 0 && <div style={{ fontSize: 11.5, color: "var(--muted)" }}>No recent news to score.</div>}
      <div style={{ display: "grid", gap: 8 }}>
        {(d?.items || []).map((it, i) => (
          <div key={i} data-testid={`triaxis-item-${i}`} style={subtleBox}>
            <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
              {it.ticker && <span style={{ fontSize: 10, fontWeight: 800, color: "#a78bfa" }}>{it.ticker}</span>}
              <span style={{ fontSize: 11.5, color: "var(--text)", fontWeight: 600, flex: 1, minWidth: 0 }}>{it.headline}</span>
              <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "rgba(167,139,250,0.15)", color: "#a78bfa", fontWeight: 800, letterSpacing: 1 }}>EDGE {it.edgeScore.toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap", fontSize: 9.5 }}>
              <span style={{ padding: "2px 6px", borderRadius: 4, background: "rgba(74,222,128,0.1)", color: "#4ade80", letterSpacing: 0.6, fontWeight: 700 }}>Materiality {(it.materiality * 100).toFixed(0)}%</span>
              <span style={{ padding: "2px 6px", borderRadius: 4, background: "rgba(96,165,250,0.1)", color: "#60a5fa", letterSpacing: 0.6, fontWeight: 700 }}>Novelty {(it.novelty * 100).toFixed(0)}%</span>
              <span style={{ padding: "2px 6px", borderRadius: 4, background: it.direction >= 0 ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)", color: it.direction >= 0 ? "#4ade80" : "#f87171", letterSpacing: 0.6, fontWeight: 700 }}>
                Direction {it.direction >= 0 ? "+" : ""}{it.direction.toFixed(2)}
              </span>
            </div>
            <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, fontStyle: "italic" }}>{it.rationale}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SectorIntelCard (PDF p.22) ───────────────────────────────────────────────
const HORIZON_COLOR = { short: "#f87171", tactical: "#fbbf24", medium: "#60a5fa", long: "#4ade80" };

export function SectorIntelCard() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setLoading(true); setErr(null);
    const stop = fastGet(
      `${API}/omega/sector-intel`,
      (data) => { setD(data); setLoading(false); },
      (e) => { setErr(_err(e)); setLoading(false); },
      { axiosOpts: { timeout: 60000 }, force: refreshKey > 0 }
    );
    return stop;
  }, [refreshKey]);

  const cacheAge = d?.generatedAt ? (() => {
    const mins = Math.round((Date.now() - new Date(d.generatedAt).getTime()) / 60000);
    if (mins < 60) return `${mins}m ago`;
    return `${Math.round(mins / 60)}h ago`;
  })() : null;

  return (
    <div className="card" data-testid="sector-intel-card" style={cardBase}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <div className="sh" style={{ margin: 0 }}>Sector Intelligence</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {d?.fromCache && cacheAge && (
            <span style={{ fontSize: 8, color: "var(--dim)", fontStyle: "italic" }}>cached {cacheAge}</span>
          )}
          {d && !d.fromCache && (
            <span style={{ fontSize: 8, color: "#4ade80" }}>fresh</span>
          )}
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            disabled={loading}
            style={{ fontSize: 9, padding: "2px 8px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", color: "var(--muted)", cursor: loading ? "not-allowed" : "pointer" }}
          >
            {loading ? "…" : "↺"}
          </button>
        </div>
      </div>
      <div style={{ fontSize: 10.5, color: "var(--muted)", marginBottom: 10 }}>
        Structural read · natural horizon · favored instruments · regime {d?.regime || "…"} · PDF p.22
      </div>
      {loading && <div style={{ fontSize: 11.5, color: "var(--muted)" }}>Reading sectors…</div>}
      {err && <div style={{ fontSize: 11, color: "#f87171" }}>{err}</div>}
      {!loading && !err && d?.sectors?.length === 0 && (
        <div style={{ fontSize: 11, color: "var(--dim)" }}>No sector data returned — try refreshing.</div>
      )}
      <div style={{ display: "grid", gap: 8 }}>
        {(d?.sectors || []).map((s, i) => (
          <div key={i} data-testid={`sector-${i}`} style={subtleBox}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginBottom: 4 }}>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: "var(--text)" }}>{s.name}</span>
              <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, background: "var(--layer2)", color: HORIZON_COLOR[s.horizon] || "var(--muted)", letterSpacing: 1, fontWeight: 800, textTransform: "uppercase" }}>{s.horizon}</span>
            </div>
            <div style={{ fontSize: 11.5, color: "var(--text)", lineHeight: 1.55, marginBottom: 6 }}>{s.structuralRead}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
              {(s.topTickers || []).map(t => <span key={t} style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "rgba(167,139,250,0.12)", color: "#a78bfa", fontWeight: 800, letterSpacing: 0.6 }}>{t}</span>)}
            </div>
            <div style={{ fontSize: 10.5, color: "var(--muted)", lineHeight: 1.55 }}>
              <b style={{ color: "var(--text)" }}>Favored:</b> {(s.favoredInstruments || []).join(" · ")} → {(s.favoredStrategies || []).join(" · ")}
            </div>
            {s.risk && <div style={{ fontSize: 10.5, color: "#f87171", marginTop: 4, fontStyle: "italic" }}>Risk: {s.risk}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── StrategySelectorCard (PDF p.21) ──────────────────────────────────────────
const VIEW_OPTS = ["bullish", "bearish", "neutral-range", "vol-spike", "structural-long"];
const HORIZON_OPTS = ["short", "tactical", "medium", "long"];
const IV_OPTS = ["rich", "cheap", "normal"];

export function StrategySelectorCard({ defaultSymbol = "" }) {
  const [form, setForm] = useState({ view: "bullish", conviction: "medium", horizon: "tactical", iv_state: "normal", account_usd: 5000, symbol: defaultSymbol });
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const run = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.post(`${API}/omega/strategy-selector`, form, { timeout: 45000 });
      setD(r.data);
    } catch (e) { setErr(_err(e)); } finally { setLoading(false); }
  }, [form]);

  const ctrlStyle = {
    padding: "6px 10px", borderRadius: 8, fontSize: 11, fontFamily: "inherit",
    background: "var(--layer1, rgba(255,255,255,0.04))",
    border: "1px solid var(--border, rgba(255,255,255,0.1))",
    color: "var(--text)", outline: "none",
  };

  return (
    <div className="card" data-testid="strategy-selector-card" style={cardBase}>
      <div className="sh" style={{ margin: 0 }}>Strategy Selector</div>
      <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, marginBottom: 10 }}>
        Pick the 2-3 strategies whose risk shape fits view × regime × account · PDF p.21
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 6, marginBottom: 10 }}>
        <select data-testid="ss-view" value={form.view} onChange={e => setForm({ ...form, view: e.target.value })} style={ctrlStyle}>
          {VIEW_OPTS.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="ss-conviction" value={form.conviction} onChange={e => setForm({ ...form, conviction: e.target.value })} style={ctrlStyle}>
          {["low", "medium", "high"].map(v => <option key={v} value={v}>{v} conv</option>)}
        </select>
        <select data-testid="ss-horizon" value={form.horizon} onChange={e => setForm({ ...form, horizon: e.target.value })} style={ctrlStyle}>
          {HORIZON_OPTS.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="ss-iv" value={form.iv_state} onChange={e => setForm({ ...form, iv_state: e.target.value })} style={ctrlStyle}>
          {IV_OPTS.map(v => <option key={v} value={v}>IV {v}</option>)}
        </select>
        <input data-testid="ss-symbol" value={form.symbol} onChange={e => setForm({ ...form, symbol: e.target.value.toUpperCase() })} placeholder="symbol" style={ctrlStyle} />
        <input data-testid="ss-account" type="number" value={form.account_usd} onChange={e => setForm({ ...form, account_usd: Number(e.target.value) })} placeholder="account $" style={ctrlStyle} />
      </div>
      <button data-testid="ss-submit" onClick={run} disabled={loading} style={{ width: "100%", padding: "8px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: "#a78bfa", color: "#fff", border: "none", cursor: loading ? "wait" : "pointer", marginBottom: 10 }}>{loading ? "Selecting…" : "Find best strategies"}</button>
      {err && <div style={{ fontSize: 11, color: "#f87171", marginBottom: 8 }}>{err}</div>}
      {d?.recommendations && (
        <div style={{ display: "grid", gap: 8 }}>
          {d.recommendations.map((r, i) => (
            <div key={i} data-testid={`ss-rec-${i}`} style={{ ...subtleBox, borderColor: i === 0 ? "rgba(167,139,250,0.4)" : "var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: "var(--text)" }}>{r.strategy}</span>
                <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 4, background: "rgba(74,222,128,0.15)", color: "#4ade80", fontWeight: 800, letterSpacing: 1 }}>FIT {r.fitScore}</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--text)", fontFamily: "monospace", marginBottom: 4 }}>{r.exampleStructure}</div>
              <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.55, marginBottom: 4 }}>{r.rationale}</div>
              <div style={{ display: "flex", gap: 8, fontSize: 10, color: "var(--dim)" }}>
                <span>Max loss: <b style={{ color: "#f87171" }}>${r.maxLossUsd}</b></span>
                <span>Max gain: <b style={{ color: "#4ade80" }}>${r.maxGainUsd}</b></span>
              </div>
            </div>
          ))}
          {d.avoid?.length > 0 && (
            <div style={{ ...subtleBox, borderColor: "rgba(248,113,113,0.2)", background: "rgba(248,113,113,0.04)" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: "#f87171", letterSpacing: 1, marginBottom: 4 }}>AVOID</div>
              {d.avoid.map((a, i) => <div key={i} style={{ fontSize: 11, color: "var(--text)", marginBottom: 2 }}>• {a}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── BestExpressionCard (PDF p.20) ────────────────────────────────────────────
export function BestExpressionCard({ defaultSymbol = "" }) {
  const [form, setForm] = useState({ thesis: "", symbol: defaultSymbol, horizon_days: 30, capital_usd: 2000 });
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const run = useCallback(async () => {
    if (form.thesis.trim().length < 8) { setErr("Thesis must be at least 8 chars."); return; }
    setLoading(true); setErr(null);
    try {
      const r = await axios.post(`${API}/omega/best-expression`, form, { timeout: 45000 });
      setD(r.data);
    } catch (e) { setErr(_err(e)); } finally { setLoading(false); }
  }, [form]);

  const ctrlStyle = {
    padding: "6px 10px", borderRadius: 8, fontSize: 11, fontFamily: "inherit",
    background: "var(--layer1, rgba(255,255,255,0.04))",
    border: "1px solid var(--border, rgba(255,255,255,0.1))",
    color: "var(--text)", outline: "none",
  };

  return (
    <div className="card" data-testid="best-expression-card" style={cardBase}>
      <div className="sh" style={{ margin: 0 }}>Best-Expression Engine</div>
      <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, marginBottom: 10 }}>
        For a thesis, find the BEST instrument — stock vs. spread vs. LEAP · PDF p.20
      </div>
      <textarea
        data-testid="bx-thesis"
        value={form.thesis}
        onChange={e => setForm({ ...form, thesis: e.target.value })}
        placeholder="State your thesis in 1–3 sentences (e.g. 'AAPL AI catalyst into earnings 4 weeks out')"
        rows={3}
        style={{ ...ctrlStyle, width: "100%", marginBottom: 8, resize: "vertical" }}
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 8 }}>
        <input data-testid="bx-symbol" value={form.symbol} onChange={e => setForm({ ...form, symbol: e.target.value.toUpperCase() })} placeholder="symbol" style={ctrlStyle} />
        <input data-testid="bx-horizon" type="number" value={form.horizon_days} onChange={e => setForm({ ...form, horizon_days: Number(e.target.value) })} placeholder="horizon days" style={ctrlStyle} />
        <input data-testid="bx-capital" type="number" value={form.capital_usd} onChange={e => setForm({ ...form, capital_usd: Number(e.target.value) })} placeholder="capital $" style={ctrlStyle} />
      </div>
      <button data-testid="bx-submit" onClick={run} disabled={loading} style={{ width: "100%", padding: "8px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: "#a78bfa", color: "#fff", border: "none", cursor: loading ? "wait" : "pointer", marginBottom: 10 }}>{loading ? "Mapping…" : "Find best expression"}</button>
      {err && <div style={{ fontSize: 11, color: "#f87171", marginBottom: 8 }}>{err}</div>}
      {d?.primaryInstrument && (
        <div style={{ display: "grid", gap: 8 }}>
          <div data-testid="bx-primary" style={{ ...subtleBox, borderColor: "rgba(167,139,250,0.4)" }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa", letterSpacing: 1, marginBottom: 4 }}>PRIMARY · {d.primaryInstrument.type.toUpperCase()}</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text)", marginBottom: 4 }}>{d.primaryInstrument.label}</div>
            {d.primaryInstrument.structure && <div style={{ fontSize: 11, color: "var(--text)", fontFamily: "monospace", marginBottom: 4 }}>{d.primaryInstrument.structure}</div>}
            <div style={{ fontSize: 11.5, color: "var(--dim)", lineHeight: 1.55, marginBottom: 4 }}>{d.primaryInstrument.reasoning}</div>
            <div style={{ display: "flex", gap: 8, fontSize: 10, color: "var(--muted)" }}>
              <span>Capital: <b style={{ color: "var(--text)" }}>${d.primaryInstrument.capitalRequired}</b></span>
              <span>vs stock: <b style={{ color: "var(--text)" }}>{d.primaryInstrument.leverageVsStock.toFixed(1)}×</b></span>
            </div>
          </div>
          {d.alternatives?.length > 0 && (
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, color: "var(--muted)", letterSpacing: 1, marginBottom: 6 }}>ALTERNATIVES</div>
              <div style={{ display: "grid", gap: 6 }}>
                {d.alternatives.map((a, i) => (
                  <div key={i} data-testid={`bx-alt-${i}`} style={subtleBox}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text)" }}>{a.label} · <span style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 1 }}>{a.type}</span></div>
                    <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 2, lineHeight: 1.5 }}>{a.reasoning}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {d.rejected?.length > 0 && (
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, color: "#f87171", letterSpacing: 1, marginBottom: 6 }}>REJECTED</div>
              <div style={{ display: "grid", gap: 6 }}>
                {d.rejected.map((r, i) => (
                  <div key={i} data-testid={`bx-rej-${i}`} style={{ ...subtleBox, borderColor: "rgba(248,113,113,0.18)" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text)" }}>{r.label} · <span style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 1 }}>{r.type}</span></div>
                    <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 2, lineHeight: 1.5 }}>{r.reasoning}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CalibrationCard (PDF p.25) ───────────────────────────────────────────────
const CALIB_TONE = {
  good: { c: "#4ade80", label: "GOOD" },
  over_confident: { c: "#f87171", label: "OVER" },
  under_confident: { c: "#fbbf24", label: "UNDER" },
};

export function CalibrationCard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => fastGet(
    `${API}/omega/calibration`,
    setD,
    (e) => setErr(_err(e)),
  ), []);

  return (
    <div className="card" data-testid="calibration-card" style={{ ...cardBase, borderColor: "rgba(74,222,128,0.28)" }}>
      <div className="sh" style={{ margin: 0 }}>Calibration · learning loop</div>
      <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, marginBottom: 10 }}>
        Are our stated probabilities matching real outcomes? · PDF p.25
      </div>
      {err && <div style={{ fontSize: 11, color: "#f87171" }}>{err}</div>}
      {d && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8, marginBottom: 12 }}>
            <div style={subtleBox}>
              <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 0.6, fontWeight: 700, textTransform: "uppercase" }}>Samples</div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>{d.samples}</div>
            </div>
            <div style={subtleBox}>
              <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 0.6, fontWeight: 700, textTransform: "uppercase" }}>Stated</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#60a5fa" }}>{d.overallStatedWinRate != null ? `${d.overallStatedWinRate}%` : "—"}</div>
            </div>
            <div style={subtleBox}>
              <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 0.6, fontWeight: 700, textTransform: "uppercase" }}>Actual</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#4ade80" }}>{d.overallActualWinRate != null ? `${d.overallActualWinRate}%` : "—"}</div>
            </div>
            <div style={subtleBox}>
              <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 0.6, fontWeight: 700, textTransform: "uppercase" }}>Sizing×</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: d.sizingMultiplier < 1 ? "#f87171" : d.sizingMultiplier > 1 ? "#4ade80" : "var(--text)" }}>{d.sizingMultiplier.toFixed(2)}</div>
            </div>
          </div>
          <div style={{ ...subtleBox, marginBottom: 12, borderColor: d.delta != null && d.delta < -5 ? "rgba(248,113,113,0.35)" : "var(--border)" }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: "var(--muted)", letterSpacing: 1, marginBottom: 4 }}>VERDICT</div>
            <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.55 }}>{d.verdict}</div>
          </div>
          {d.buckets?.length > 0 && (
            <>
              <div style={{ fontSize: 9, fontWeight: 800, color: "var(--muted)", letterSpacing: 1, marginBottom: 6 }}>BY ZI-SCORE BUCKET</div>
              <div style={{ display: "grid", gap: 6 }}>
                {d.buckets.map((b, i) => {
                  const tone = CALIB_TONE[b.status] || CALIB_TONE.good;
                  return (
                    <div key={i} data-testid={`calib-bucket-${i}`} style={{ ...subtleBox, padding: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text)" }}>Zi {b.bucket}</span>
                        <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: `${tone.c}22`, color: tone.c, letterSpacing: 1 }}>{tone.label}</span>
                      </div>
                      <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 2 }}>
                        stated <b style={{ color: "var(--text)" }}>{b.statedProbabilityMid}%</b> · actual <b style={{ color: "var(--text)" }}>{b.actualWinRate}%</b> · Δ {b.delta >= 0 ? "+" : ""}{b.delta} · n={b.n} · avg pnl ${b.avgPnl}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── WorkedExampleCard (PDF p.28) ─────────────────────────────────────────────
export function WorkedExampleCard({ defaultSymbol = "AAL" }) {
  const [symbol, setSymbol] = useState(defaultSymbol);
  const [months, setMonths] = useState(6);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const run = useCallback(async () => {
    if (!symbol) return;
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/omega/worked-example?symbol=${symbol}&horizon_months=${months}`, { timeout: 70000 });
      setD(r.data);
    } catch (e) { setErr(_err(e)); } finally { setLoading(false); }
  }, [symbol, months]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);

  return (
    <div className="card" data-testid="worked-example-card" style={cardBase}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
        <div className="sh" style={{ margin: 0 }}>Worked Example · {symbol}</div>
        <div style={{ display: "flex", gap: 4 }}>
          <input data-testid="we-symbol" value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())} style={{ width: 70, padding: "4px 8px", borderRadius: 6, fontSize: 11, background: "var(--layer1)", border: "1px solid var(--border)", color: "var(--text)", outline: "none", fontFamily: "inherit" }} />
          <input data-testid="we-months" type="number" value={months} onChange={e => setMonths(Math.max(1, Math.min(24, Number(e.target.value))))} style={{ width: 60, padding: "4px 8px", borderRadius: 6, fontSize: 11, background: "var(--layer1)", border: "1px solid var(--border)", color: "var(--text)", outline: "none", fontFamily: "inherit" }} />
          <button data-testid="we-go" onClick={run} disabled={loading} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: "#a78bfa", color: "#fff", border: "none", cursor: loading ? "wait" : "pointer" }}>{loading ? "…" : "GO"}</button>
        </div>
      </div>
      <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, marginBottom: 10 }}>
        Sense → Council → Engine → Brief · the full reasoning chain · PDF p.28
      </div>
      {err && <div style={{ fontSize: 11, color: "#f87171" }}>{err}</div>}
      {loading && !d && <div style={{ fontSize: 11.5, color: "var(--muted)" }}>Running the reasoning chain…</div>}
      {d && (
        <div style={{ display: "grid", gap: 10 }}>
          {/* Sensing */}
          <div data-testid="we-sensing" style={subtleBox}>
            <div style={{ fontSize: 9, fontWeight: 800, color: "#60a5fa", letterSpacing: 1, marginBottom: 4 }}>1 · SENSING</div>
            <div style={{ fontSize: 11.5, color: "var(--text)", lineHeight: 1.55, marginBottom: 6 }}>{d.sensing.summary}</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {d.sensing.bullets.map((b, i) => <span key={i} style={{ fontSize: 9.5, padding: "2px 6px", borderRadius: 4, background: "rgba(96,165,250,0.1)", color: "#60a5fa" }}>{b}</span>)}
            </div>
          </div>
          {/* Council */}
          <div data-testid="we-council" style={subtleBox}>
            <div style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa", letterSpacing: 1, marginBottom: 6 }}>2 · COUNCIL</div>
            <div style={{ display: "grid", gap: 6 }}>
              {[
                { k: "quant", label: "Quant", c: "#4ade80" },
                { k: "macro", label: "Macro Strategist", c: "#60a5fa" },
                { k: "risk", label: "Risk Officer", c: "#fbbf24" },
                { k: "devilsAdvocate", label: "Devil's Advocate", c: "#f87171" },
              ].map(({ k, label, c }) => (
                <div key={k} data-testid={`we-council-${k}`} style={{ padding: 8, borderRadius: 8, background: `${c}0a`, borderLeft: `2px solid ${c}` }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: c, letterSpacing: 1, marginBottom: 2 }}>{label.toUpperCase()}</div>
                  <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.5 }}>{d.council[k]}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Engine */}
          <div data-testid="we-engine" style={subtleBox}>
            <div style={{ fontSize: 9, fontWeight: 800, color: "#4ade80", letterSpacing: 1, marginBottom: 6 }}>3 · ENGINE · QUANT</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))", gap: 6, marginBottom: 6 }}>
              <div><div style={{ fontSize: 8, color: "var(--dim)", letterSpacing: 1 }}>POP</div><div style={{ fontSize: 16, fontWeight: 800, color: "#4ade80" }}>{d.engine.popPct}%</div></div>
              <div><div style={{ fontSize: 8, color: "var(--dim)", letterSpacing: 1 }}>P10</div><div style={{ fontSize: 13, fontWeight: 700, color: "#f87171" }}>${d.engine.p10}</div></div>
              <div><div style={{ fontSize: 8, color: "var(--dim)", letterSpacing: 1 }}>MEDIAN</div><div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>${d.engine.medianPrice}</div></div>
              <div><div style={{ fontSize: 8, color: "var(--dim)", letterSpacing: 1 }}>P90</div><div style={{ fontSize: 13, fontWeight: 700, color: "#4ade80" }}>${d.engine.p90}</div></div>
            </div>
            {d.engine.leftTailDrivers?.length > 0 && (
              <div style={{ fontSize: 10, color: "var(--muted)" }}>Left-tail drivers: <b style={{ color: "#f87171" }}>{d.engine.leftTailDrivers.join(" · ")}</b></div>
            )}
          </div>
          {/* Brief */}
          <div data-testid="we-brief" style={{ ...subtleBox, borderColor: "rgba(167,139,250,0.4)" }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa", letterSpacing: 1, marginBottom: 4 }}>4 · OMEGA'S BRIEF</div>
            <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.65 }}>{d.brief}</div>
          </div>
        </div>
      )}
    </div>
  );
}
